import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import get from 'lodash.get'
import { makeStyles } from '@material-ui/core'
import { useQueryClient } from '@tanstack/react-query'
import { useDDPInspectorContext } from '../DDPInspectorContext'
import { parseAccessor } from '../utils/parseAccessor'
import { pickExampleRow } from '../utils/pickExampleRow'
import { getCachedPipelineData } from '../utils/getCachedPipelineData'
import { formatExampleValue } from '../utils/formatExampleValue'

const useStyles = makeStyles(() => ({
  empty: {
    padding: '14px 12px',
    color: 'var(--color-neutral-500)',
    fontSize: 13,
    fontStyle: 'italic',
    border: '1px dashed var(--color-neutral-200)',
    borderRadius: 'var(--border-radius-sm, 4px)'
  },
  componentBlock: {
    border: '1px solid var(--color-neutral-200)',
    borderRadius: 'var(--border-radius-sm, 4px)'
  },
  componentHeader: {
    padding: '8px 12px',
    backgroundColor: 'var(--color-neutral-050)',
    color: 'var(--color-neutral-800)',
    fontSize: 12
  },
  componentName: {
    padding: '1px 6px',
    fontSize: 11,
    borderRadius: 3,
    backgroundColor: 'var(--color-primary-100, #dbeafe)',
    color: 'var(--color-primary-800, #1e40af)'
  },
  componentLabel: {
    fontSize: 12,
    color: 'var(--color-neutral-700)'
  },
  componentCount: {
    fontSize: 10,
    color: 'var(--color-neutral-500)',
    letterSpacing: '0.4px'
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: 11
  },
  th: {
    textAlign: 'left',
    padding: '4px 8px',
    fontSize: 10,
    letterSpacing: '0.4px',
    color: 'var(--color-neutral-500)',
    borderBottom: '1px solid var(--color-neutral-200)',
    backgroundColor: 'var(--color-neutral-050)'
  },
  td: {
    padding: '4px 8px',
    borderBottom: '1px solid var(--color-neutral-100)',
    color: 'var(--color-neutral-800)',
    verticalAlign: 'top'
  },
  path: {
    color: 'var(--color-primary-800, #1e40af)'
  },
  meta: {
    color: 'var(--color-neutral-500)',
    fontSize: 10
  },
  note: {
    color: 'var(--color-warning-700, #a16207)',
    fontSize: 10,
    fontStyle: 'italic'
  }
}))

export default function AccessorsTab ({ entry, orionRequests }) {
  const classes = useStyles()
  const queryClient = useQueryClient()
  const { componentAccessorsByEntryId } = useDDPInspectorContext()
  const components = useMemo(() => {
    const componentMap = (entry && componentAccessorsByEntryId?.[entry.id]) || {}
    return Object.values(componentMap)
  }, [entry, componentAccessorsByEntryId])
  const cachedData = useMemo(() => getCachedPipelineData(queryClient, entry), [queryClient, entry])

  if (!components.length) {
    return (
      <div className={`${classes.empty} text-center`}>
        No DDP component on this page reported any accessors yet. DDPTable and
        DDPMetric publish their accessors automatically; other components will
        appear once they opt in.
      </div>
    )
  }

  return (
    <>
      {components.map((c, idx) => {
        const exampleRow = pickExampleRow(cachedData, c.dataAccessor, c.accessors || [])
        return (
          <div key={idx} className={`${classes.componentBlock} mb-3 overflow-hidden`}>
            <div className={`${classes.componentHeader} flex items-center gap-2`}>
              <span className={`${classes.componentName} font-bold font-mono`}>{c.component}</span>
              {c.label && <span className={`${classes.componentLabel} font-medium`}>{c.label}</span>}
              <span className={`${classes.componentCount} font-bold uppercase ml-auto`}>{(c.accessors || []).length} accessor{(c.accessors || []).length === 1 ? '' : 's'}</span>
            </div>
            <table className={`${classes.table} font-mono mt-1`}>
              <thead>
                <tr>
                  <th className={`${classes.th} font-bold uppercase`}>label</th>
                  <th className={`${classes.th} font-bold uppercase`}>accessor</th>
                  <th className={`${classes.th} font-bold uppercase`}>orion calc</th>
                  <th className={`${classes.th} font-bold uppercase`}>example value</th>
                </tr>
              </thead>
              <tbody>
                {(c.accessors || []).map((a, i) => {
                  const parsed = orionRequests?.length ? parseAccessor(a.accessor, orionRequests) : null
                  const exampleValue = exampleRow ? get(exampleRow, a.accessor) : undefined
                  return (
                    <tr key={`${a.accessor}-${i}`}>
                      <td className={classes.td}>{a.label || '—'}</td>
                      <td className={classes.td}>
                        <span className={`${classes.path} font-bold`}>{a.accessor}</span>
                      </td>
                      <td className={classes.td}>
                        {parsed
                          ? (
                            <>
                              <span>{parsed.calculationId || '—'}</span>
                              {parsed.calculationType && (
                                <div className={`${classes.meta} font-mono`}>{parsed.calculationType}{parsed.field ? ` · ${parsed.field}` : ''}</div>
                              )}
                              {!parsed.matched && (
                                <div className={classes.note}>not found in orion calculations</div>
                              )}
                            </>
                          )
                          : '—'}
                      </td>
                      <td className={classes.td}>
                        <span className={`${classes.meta} font-mono`}>{formatExampleValue(exampleValue)}</span>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )
      })}
    </>
  )
}

AccessorsTab.propTypes = {
  entry: PropTypes.object,
  orionRequests: PropTypes.array
}
